import React from "react";
import cn from "classnames";
import { createUseStyles } from "react-jss";
import { Popover, Transition } from '@headlessui/react';

import UIIcon from "~/old-ui/UIIcon";
import useStorageState from "~/hooks/useStorageState";
import { truncateString } from "~/utils";
import { smallFontSize, secondaryTextColor } from "~/theme";


const blogAPIUrl = 'https://workshopbutler.com/blog/ghost/api/';
const contentKey = '7ef3536d401968a1c7c6ea6ec9';
const maxCount = 5;

const postsQuery = `${blogAPIUrl}/content/posts/?key=${contentKey}&include=tags&filter=tags:[hash-update]&order=published_at+desc&limit=${maxCount}`;

export default function NavbarUpdatesWidget() {
  const classes = useStyles();
  const [posts, setPosts] = React.useState(undefined);
  const [shownPosts, setShownPosts] = useStorageState(
    [localStorage, `updates:${contentKey}:widget:posts:shown`],
    []
  );
  const unshownPostsCount = (posts !== undefined) ? posts.filter(post => !post.hasShown).length : undefined;

  const markAsSeen = React.useCallback(() => {
    setShownPosts(posts.map(post => post.id));
    setPosts(posts.map(post => ({
      ...post,
      hasShown: true,
    })))
  }, [posts]);

  const fetchPosts = () => {
    fetch(postsQuery, {
      method: 'GET',
    }).then((response) => {
      if (response.ok) {
        response.json().then((data) => {
          setPosts(
            data.posts.map(post => ({
              id: post.id,
              title: post.title,
              excerpt: post.excerpt,
              permalink: post.url,
              labels: post.tags.filter(t => t.visibility === "public").map(
                tag => ({ name: tag.name, color: tag.accent_color || 'rgb(120, 129, 136)' })),
              hasShown: shownPosts.includes(post.id),
              publicationTime: Date.parse(post.published_at),
            }))
          );
        });
      }
    })
  }

  React.useEffect(() => {
    fetchPosts();
  }, [contentKey]);

  return (
    <Popover className="tailwind relative z-50">
      <Popover.Button className="w-6 h-6 flex items-center justify-center"
        onClick={() => {
          if (!posts) {
            fetchPosts();
          } else if (unshownPostsCount > 0) {
            markAsSeen();
          }
        }}>
        <UIIcon name='fa-bullhorn' className={unshownPostsCount > 0 ? cn(classes.icon, classes.iconAlert) : classes.icon} />
      </Popover.Button>
      <Transition
        as={React.Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <Popover.Panel className="absolute z-10 w-80 mt-4 transform -translate-x-1/2 left-1
                bg-white rounded-md shadow-lg ring-1 ring-gray ring-opacity-20 overflow-hidden ">
          {posts && (
            <h3 className={classes.dropDownMenuTitle}>Product Updates</h3>
          )}

          {posts ? posts.map(post => {
            const postShortExcerpt = truncateString(post.excerpt || '', 140 - post.title.length);

            return (
              <a
                className={classes.post}
                key={post.id}
                href={post.permalink+'?utm_source=app&utm_medium=changelogItem&utm_campaign=updates'}
                target='_blank'
              >

                <span className='font-bold'>{post.title}</span> <span className='color-black'>{postShortExcerpt}</span>
                <div>{
                  post.labels.map(label =>
                    <span style={{
                      backgroundColor: label.color,
                      color: '#eee',
                    }} className={classes.postLabel} key={label.name}>
                      {label.name}
                    </span>
                  )
                }</div>
              </a>
            )
          }) : (
            <div className="py-6 px-4">Loading...</div>
          )}

          {posts && (
            <div className={classes.fullChangelogDiv}>
              <a
                className={classes.fullChangelogLink}
                href='https://workshopbutler.com/updates/?utm_source=app&utm_medium=fullChangelog&utm_campaign=updates'
                target='_blank'
              >Full changelog</a>
            </div>
          )}
        </Popover.Panel>
      </Transition>
    </Popover>
  )
}


const useStyles = createUseStyles({

  icon: {
    color: '#fff',
    width: 16,
    height: 16,
    cursor: 'pointer',
    outline: 'none',
  },

  iconAlert: {
    color: ['#F00', '!important'],
    animation: 'shake 5s linear infinite',
  },

  dropDownMenuTitle: {
    color: secondaryTextColor,
    margin: [[16, 0, 16, 16]],
    fontSize: smallFontSize,
    fontWeight: 400,
    textTransform: 'uppercase',
  },

  post: {
    display: 'block',
    color: 'inherit',
    textDecoration: 'none',
    padding: [[4, 16, 4, 16]],
    borderLeft: [[2, 'solid', 'rgb(255, 112, 67)']],
    //borderBottom: [[1, 'solid', '#e5e5e5']],
    margin: [[8, 0, 8, 0]],

    '&:hover, &:focus': {
      backgroundColor: '#eee',
      textDecoration: 'none'
    },
  },

  postLabel: {
    display: 'inline-block',
    fontSize: smallFontSize,
    margin: 0,
    borderRadius: 2,
    fontWeight: 600,
    lineHeight: 'normal',
    marginRight: 4,
    padding: [[1, 4]],
    textTransform: 'uppercase',
  },

  fullChangelogDiv: {
    padding: [[12, 20]],
    textAlign: 'center',
  },

  fullChangelogLink: {
    color: secondaryTextColor,

    '&:hover, &:focus': {
      textDecoration: 'none'
    }
  }
});

